// RITA-1542 (Hauptticket RITA-901) — Server-PDF einer Vertragsvorlage abrufen.
//
// Der Server (POST /contract-templates/pdf, apps/backend/src/modules/templates/contractTemplatePdfRouter.ts)
// erzeugt aus dem Editor-Dokument und den Platzhalterwerten eine PDF/A. Der Abruf läuft außerhalb von GraphQL
// mit derselben Anmeldung wie die geschützte Ablage (authorizationHeader, RITA-1538). Fehlerantworten werden
// hier in feste Nutzertexte übersetzt; technische Einzelheiten stehen nie im Text.
import { ANMELDUNG_ABGELAUFEN_HINWEIS, istAnmeldungAbgelaufen } from '../../../shared/services/anmeldungAbgelaufen';
import authorizationHeader from '../../../shared/services/authorizationHeader';
import type { Vertragsdokument } from '../contractEditor/model/types';

export const VERTRAGSVORLAGE_PDF_ENDPUNKT = '/contract-templates/pdf';

export const PDF_PROBE_FEHLER = {
  /** Wie bei der geschützten Ablage (RITA-1538, Variante B): Hinweis statt Abmeldung. */
  anmeldung: ANMELDUNG_ABGELAUFEN_HINWEIS,
  berechtigung: 'Die PDF konnte nicht erstellt werden, weil die Berechtigung fehlt.',
  dokument: 'Die PDF konnte nicht erstellt werden, weil der Aufbau des Vertrags ungültig ist.',
  zuGross:
    'Die PDF konnte nicht erstellt werden, weil der Vertrag zu groß ist. Bitte kürzen oder auf mehrere Vorlagen aufteilen.',
  allgemein: 'Die PDF konnte nicht erstellt werden. Bitte später erneut versuchen.',
  verbindung:
    'Die PDF konnte nicht erstellt werden, weil der Server nicht erreichbar ist. Bitte später erneut versuchen.',
} as const;

export interface VertragsvorlagePdfAuftrag {
  dokument: Vertragsdokument;
  werte: Record<string, string>;
  titel: string;
}

export class VertragsvorlagePdfFehler extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'VertragsvorlagePdfFehler';
    this.status = status;
    // Zielumgebung ES5: die Prototypkette von Error-Unterklassen von Hand setzen.
    Object.setPrototypeOf(this, VertragsvorlagePdfFehler.prototype);
  }
}

const fehlertext = (status: number, antwort: { error?: string; code?: string; message?: string } | null): string => {
  if (istAnmeldungAbgelaufen(status)) return PDF_PROBE_FEHLER.anmeldung;
  if (status === 403) return PDF_PROBE_FEHLER.berechtigung;
  // Zu gross: entweder die Rumpfgrenze (413) oder die fachliche Dokumentgrenze (400 mit Code DOCUMENT_TOO_LARGE).
  if (status === 413 || antwort?.error === 'too_large' || antwort?.code === 'DOCUMENT_TOO_LARGE') {
    return PDF_PROBE_FEHLER.zuGross;
  }
  // Die Servermeldung stammt aus der Speicherpruefung („… konnte nicht gespeichert werden …“) und passt hier nicht.
  if (status === 400 && antwort?.error === 'invalid_document') return PDF_PROBE_FEHLER.dokument;
  return PDF_PROBE_FEHLER.allgemein;
};

/** Fordert die PDF an und liefert sie als Blob; bei Fehlern ein VertragsvorlagePdfFehler mit Nutzertext. */
export async function vertragsvorlagePdfAbrufen(
  auftrag: VertragsvorlagePdfAuftrag,
  fetchImpl: typeof fetch = (eingabe, init) => fetch(eingabe, init),
): Promise<Blob> {
  let antwort: Response;
  try {
    antwort = await fetchImpl(VERTRAGSVORLAGE_PDF_ENDPUNKT, {
      method: 'POST',
      headers: { ...authorizationHeader(), 'content-type': 'application/json', accept: 'application/pdf' },
      body: JSON.stringify({ document: auftrag.dokument, values: auftrag.werte, title: auftrag.titel }),
    });
  } catch (error) {
    throw new VertragsvorlagePdfFehler(PDF_PROBE_FEHLER.verbindung, null);
  }
  if (!antwort.ok) {
    let daten: { error?: string; code?: string; message?: string } | null = null;
    try {
      daten = await antwort.json();
    } catch (error) {
      daten = null;
    }
    throw new VertragsvorlagePdfFehler(fehlertext(antwort.status, daten), antwort.status);
  }
  const blob = await antwort.blob();
  // Der Blob-Typ bestimmt, wie der neue Tab die Datei anzeigt; der Server liefert application/pdf.
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}

export default vertragsvorlagePdfAbrufen;
